import { daysBetween, shiftDayKey, type DayKey } from '../lib/time'

/**
 * A repeating cadence — "Sprint, two weeks, starting the 5th" — from which every
 * iteration is derived rather than stored one by one. Individual iterations that
 * do not fit the pattern are listed separately and win where they overlap.
 */
export interface IterationCadence {
  name: string
  startDay: DayKey
  lengthDays: number
  repeat: boolean
}

export interface CustomIteration {
  id: string
  name: string
  startDay: DayKey
  /** Inclusive: an iteration ending on the 18th includes the 18th. */
  endDay: DayKey
}

/** `users/{uid}/settings/iterations` */
export interface IterationConfig {
  cadence: IterationCadence | null
  custom: CustomIteration[]
}

export interface Iteration {
  id: string
  name: string
  startDay: DayKey
  endDay: DayKey
}

export const DEFAULT_ITERATIONS: IterationConfig = { cadence: null, custom: [] }

export const MAX_ITERATION_LENGTH = 90

/** A missing length falls back to a fortnight; a nonsense one is clamped, not replaced. */
function clampLength(value: number): number {
  const rounded = Math.round(value)
  if (!Number.isFinite(rounded)) return 14
  return Math.min(MAX_ITERATION_LENGTH, Math.max(1, rounded))
}

export function normaliseIterations(raw: Partial<IterationConfig> | null | undefined): IterationConfig {
  const cadence = raw?.cadence
  return {
    cadence:
      cadence && cadence.startDay
        ? {
            name: cadence.name || 'Iteration',
            startDay: cadence.startDay,
            lengthDays: clampLength(cadence.lengthDays),
            repeat: cadence.repeat !== false,
          }
        : null,
    custom: (raw?.custom ?? [])
      .filter((entry) => entry && entry.id && entry.startDay && entry.endDay)
      .map((entry) => ({
        ...entry,
        // A backwards range is a typo, not a reason to render nothing.
        ...(daysBetween(entry.startDay, entry.endDay) < 0
          ? { startDay: entry.endDay, endDay: entry.startDay }
          : {}),
      }))
      .sort((a, b) => a.startDay.localeCompare(b.startDay)),
  }
}

function overlaps(a: { startDay: DayKey; endDay: DayKey }, b: { startDay: DayKey; endDay: DayKey }) {
  return a.startDay <= b.endDay && b.startDay <= a.endDay
}

/** The nth iteration the cadence produces, counting from its start day. */
export function cadenceIteration(cadence: IterationCadence, index: number): Iteration {
  const startDay = shiftDayKey(cadence.startDay, index * cadence.lengthDays)
  return {
    id: `cadence-${index}`,
    name: `${cadence.name} ${index + 1}`,
    startDay,
    endDay: shiftDayKey(startDay, cadence.lengthDays - 1),
  }
}

/**
 * Every iteration touching [from, to], newest last. Cadence iterations only run
 * forwards from the cadence start — there is no sprint before the first one —
 * and a generated one that collides with an explicit iteration gives way to it.
 */
export function iterationsInRange(
  config: IterationConfig,
  from: DayKey,
  to: DayKey,
): Iteration[] {
  const custom: Iteration[] = config.custom.filter((entry) => overlaps(entry, { startDay: from, endDay: to }))
  const generated: Iteration[] = []

  const { cadence } = config
  if (cadence) {
    const lastIndex = cadence.repeat
      ? Math.floor(daysBetween(cadence.startDay, to) / cadence.lengthDays)
      : 0
    const firstIndex = Math.max(
      0,
      Math.floor(daysBetween(cadence.startDay, from) / cadence.lengthDays),
    )
    for (let index = firstIndex; index <= lastIndex; index++) {
      const iteration = cadenceIteration(cadence, index)
      if (!overlaps(iteration, { startDay: from, endDay: to })) continue
      if (custom.some((entry) => overlaps(entry, iteration))) continue
      generated.push(iteration)
    }
  }

  return [...custom, ...generated].sort((a, b) => a.startDay.localeCompare(b.startDay))
}

/** Which iteration a day falls in. An explicit one wins over the cadence. */
export function iterationForDay(config: IterationConfig, day: DayKey): Iteration | null {
  const explicit = config.custom.find((entry) => entry.startDay <= day && day <= entry.endDay)
  if (explicit) return explicit

  const { cadence } = config
  if (!cadence) return null
  const offset = daysBetween(cadence.startDay, day)
  if (offset < 0) return null
  const index = Math.floor(offset / cadence.lengthDays)
  if (!cadence.repeat && index > 0) return null
  return cadenceIteration(cadence, index)
}

/** Every logical day an iteration covers, for feeding the Insights range. */
export function iterationDays(iteration: Iteration): DayKey[] {
  const days: DayKey[] = []
  for (let cursor = iteration.startDay; cursor <= iteration.endDay; cursor = shiftDayKey(cursor, 1)) {
    days.push(cursor)
  }
  return days
}

export function newIterationId(taken: Set<string>): string {
  for (let n = 1; ; n++) {
    const candidate = `it${n}`
    if (!taken.has(candidate)) return candidate
  }
}

const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * Why an iteration config is not ready to be written, or null when it is. Same
 * contract as `taxonomyProblem`: a half-typed date holds the write rather than
 * failing it.
 */
export function iterationsProblem(draft: IterationConfig): string | null {
  const { cadence } = draft
  if (cadence) {
    if (!cadence.name.trim()) return 'The cadence needs a name.'
    if (!DAY_KEY_PATTERN.test(cadence.startDay)) return 'The cadence needs a start day.'
    if (!Number.isInteger(cadence.lengthDays) || cadence.lengthDays < 1) {
      return 'An iteration has to be at least a day long.'
    }
    if (cadence.lengthDays > MAX_ITERATION_LENGTH) {
      return `An iteration cannot be longer than ${MAX_ITERATION_LENGTH} days.`
    }
  }
  for (const entry of draft.custom) {
    if (!entry.name.trim()) return 'Every one-off needs a name.'
    if (!DAY_KEY_PATTERN.test(entry.startDay) || !DAY_KEY_PATTERN.test(entry.endDay)) {
      return `${entry.name} needs both dates.`
    }
    if (entry.endDay < entry.startDay) return `${entry.name} ends before it starts.`
  }
  return null
}
